import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import AutomationConditionsTable, {
	AutomationConditionsConfig,
	convertAutomationConditionValue
} from 'pages/Automation/AutomationConditionsTable';

import { AUTOCOMPLETE, ENUMS, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author @omerrkosar
 */

const FeasibilityRulesForm = NiceModal.create(({ id, modalName }) => {
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					ruleName: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					shipmentType: yup.string().nullable().required(),
					conditions: yup.array().of(
						yup.object().shape({
							condition: yup.string().nullable().required(),
							operator: yup
								.string()
								.nullable()
								.when('condition', ([condition], schema) => {
									const item = AutomationConditionsConfig.find((item) => item.name === condition);
									return item?.type ? schema.required() : schema;
								}),

							value: yup.mixed().nullable().required()
						})
					),
					action: yup.string().nullable().required(),
					deliveryCompanySettingIDs: yup.array().nullable().required()
				})
				.required(),
		[]
	);

	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const isAuthorized = useAccessControl();

	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			ruleName: null,
			status: null,
			shipmentType: null,
			conditions: [{ condition: null, operator: null, value: null }],
			action: null,
			deliveryCompanySettingIDs: null
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		setError,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const fillFeasibilityFormInEdit = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_FEASIBILITY_RULE, { ID: id });
			const { data } = response;

			reset({
				ruleName: data.name ?? null,
				status: data.status ?? null,
				shipmentType: data.shipmentType ?? null,
				conditions: data.conditions?.map((item) => {
					const parameter = AutomationConditionsConfig.find((condition) => condition.name === item.condition);

					return {
						condition:
							AutomationConditionsConfig.find(
								(condition) =>
									condition?.nameV1 === item.condition || condition?.nameV12 === item.condition
							)?.name ?? item.condition,
						operator: item.operator ?? null,
						value: convertAutomationConditionValue(item.value, parameter) ?? null,
						ID: item.ID ?? null
					};
				}),
				action: data.actions ?? null,
				deliveryCompanySettingIDs: data.deliveryCompanySettingIDs?.split(',').map((val) => Number(val)) ?? null
			});
		} catch (error) {
			AppLogger.error(error);
			showError({});
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (id) {
			fillFeasibilityFormInEdit();
		}
	}, [id]);

	const onSubmit = (data) => {
		setIsLoading(true);
		const URL = id ? SHIPPING_AUTOMATION.UPDATE_FEASIBILITY_RULE : SHIPPING_AUTOMATION.ADD_FEASIBILITY_RULE;

		axiosPrivate
			.post(URL, {
				...(id && { ID: id }),
				...data,
				name: data.ruleName,
				conditions: data.conditions?.map((item) => ({
					ID: item.ID ? item.ID : null,
					parameter: item.condition,
					parameterCondition: item.operator === 'equalCustom' ? 'equal' : item.operator,
					parameterValue: Array.isArray(item.value)
						? item.value.map((value) => `${value.toString()}`).toString()
						: item.value.toString()
				}))
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.FEASIBILITY_RULES));
					closeModal();
				} else AppLogger.error(response);
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.SA003') {
					showError({
						header: 'nameAlreadyExists',
						message: 'error.SA003'
					});
					setError('ruleName', {
						type: 'manual',
						message: 'error.SA003'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const defaultColSpan = {
		md: 6
	};
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const deliveryCompanyAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<Modal
			fullwidth
			maxWidth="large"
			name={modalName}
			className="modal-feasibilityRule"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={id ? 'editFeasibilityRule' : 'addFeasibilityRule'}
					tooltip={id ? watch('ruleName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{id ? 'editFeasibilityRule' : 'addFeasibilityRule'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="ruleName" isLoading={isLoading} required>
						<StringInput name={'ruleName'} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							valueRemovable={false}
							placeholder="selectOne"
							searchable={false}
							name="status"
							enumName={ENUMS.FEASIBILITY_STATUS}
							value={watch('status')}
							setValue={(value) => {
								setValue('status', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="shipmentType" isLoading={isLoading} required>
						<EnumSelect
							valueRemovable={false}
							searchable={false}
							placeholder="selectOne"
							name="shipmentType"
							enumName={ENUMS.SHIPMENT_TYPE}
							value={watch('shipmentType')}
							setValue={(value) => {
								setValue('shipmentType', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="feasibility.conditions" required>
						<AutomationConditionsTable
							isLoading={isLoading}
							conditionsEnumName={
								isAuthorized('EnterprisePackage')
									? ENUMS.FEASIBILITY_CONDITIONS_ENTERPRISE
									: ENUMS.FEASIBILITY_CONDITIONS_SCALE
							}
						/>
					</FormItem>

					<FormItem label="feasibility.actions" required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							required
							placeholder="selectOne"
							name="action"
							enumName={ENUMS.DC_FEASIBILITY_RULE_ACTIONS}
							value={watch(`action`)}
							setValue={(value) => {
								setValue(`action`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem label="deliveryCompanySetting" required>
						<AutoComplete
							name="deliveryCompanySettingIDs"
							placeholder="selectOne"
							required
							multipleSelection
							autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS}
							setValue={(value) => {
								setValue(
									'deliveryCompanySettingIDs',
									value?.map((item) => item?.ID),
									{ shouldValidate: true, shouldDirty: true }
								);
							}}
							value={watch('deliveryCompanySettingIDs')}
							columns={deliveryCompanyAutocompleteColumns}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					label={id ? 'save' : 'button.add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					loading={isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

export default FeasibilityRulesForm;
